import React, { useEffect, useRef } from "react";
import "./progressSegment.scss";

interface ProgressSegmentProps {
  totalQuestions: number;
  currentQuestion: number;
  onQuestionClick: (questionIndex: number) => void;
  isRoundStarted: boolean;
  isRoundFinished: boolean;
}

const ProgressSegment: React.FC<ProgressSegmentProps> = ({
  totalQuestions,
  currentQuestion,
  onQuestionClick,
  isRoundStarted,
  isRoundFinished,
}) => {
  const segmentWidthPx = 55;
  const totalSegments = totalQuestions + 2;
  const progressWidth = segmentWidthPx * (currentQuestion + 2);

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollContainerTo = (index: number) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollX =
      segmentWidthPx * (index + 1) -
      container.clientWidth / 2 +
      segmentWidthPx / 2;

    container.scrollTo({
      left: Math.max(scrollX, 0),
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollContainerTo(currentQuestion);
  }, [currentQuestion]);

  const handleSegmentNavigation = (index: number) => {
    if (isRoundStarted && index === -1) return;
    onQuestionClick(index);
  };

  const handleItemClick = (index: number) => {
    handleSegmentNavigation(index);
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const barWidth = rect.width;
    const clickPosition = clickX / barWidth;
    const segmentIndex = Math.floor(clickPosition * (totalQuestions + 2)) - 1;
    const validIndex = Math.max(-1, Math.min(segmentIndex, totalQuestions));

    handleSegmentNavigation(validIndex);
  };

  return (
    <div className="progress-scroll-wrapper" ref={containerRef}>
      <div className="progress-scroll-inner">
        <div
          className="progress-bar"
          style={{
            cursor: "pointer",
            width: `${segmentWidthPx * totalSegments}px`,
          }}
          onClick={handleProgressBarClick}
        >
          {Array.from({ length: totalQuestions + 1 }, (_, i) => (
            <div
              key={i}
              className="progress-divider"
              style={{
                left: `${segmentWidthPx * (i + 1)}px`,
              }}
            ></div>
          ))}
          <div
            className="progress-fill"
            style={{
              width: `${progressWidth}px`,
            }}
          ></div>
        </div>
        <div
          className="progress-items"
          style={{ width: `${segmentWidthPx * totalSegments}px` }}
        >
          <div
            className={`progress-item start ${
              currentQuestion === -1 ? "active" : ""
            }`}
            onClick={() => handleItemClick(-1)}
            style={{
              cursor: isRoundStarted ? "not-allowed" : "pointer",
              opacity: isRoundStarted ? 0.5 : 1,
            }}
          >
            Start
          </div>
          {Array.from({ length: totalQuestions }, (_, index) => (
            <div
              key={index}
              className={`progress-item ${
                currentQuestion === index ? "active" : ""
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => handleItemClick(index)}
            >
              Q: {String(index + 1).padStart(2, "0")}
            </div>
          ))}
          <div
            className={`progress-item submit ${
              currentQuestion === totalQuestions ? "active" : ""
            }`}
            style={{ cursor: "pointer" }}
            onClick={() => handleItemClick(totalQuestions)}
          >
            {isRoundFinished ? "Result" : "Submit"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressSegment;
